// Camera detection utilities

const VIRTUAL_CAMERA_KEYWORDS = [
  'obs virtual',
  'obs-camera',
  'snap camera',
  'manycam',
  'xsplit',
  'streamlabs',
  'nvidia broadcast',
  'camtwist',
  'mmhmm',
  'prism live',
  'loom',
];

/**
 * Check if a camera label indicates a virtual/software camera
 */
export const isVirtualCamera = (label: string): boolean => {
  const lowerLabel = label.toLowerCase();
  return VIRTUAL_CAMERA_KEYWORDS.some(keyword => lowerLabel.includes(keyword));
};

/**
 * Filter out virtual cameras from a list of video devices
 */
export const filterRealCameras = (devices: MediaDeviceInfo[]): MediaDeviceInfo[] => {
  return devices.filter(device => {
    if (device.kind !== 'videoinput') return false;
    if (!device.label) return true; // Include if no label (permissions not granted yet)
    return !isVirtualCamera(device.label);
  });
};

/**
 * Check if the device has a real (non-virtual) camera
 */
export const hasRealCamera = async (): Promise<boolean> => {
  try {
    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
      return false;
    }
    const devices = await navigator.mediaDevices.enumerateDevices();
    const realCameras = filterRealCameras(devices);
    return realCameras.length > 0;
  } catch {
    return false;
  }
};

/**
 * Check if the device likely supports camera capture via input element
 * This is typically true on mobile devices with cameras
 */
export const supportsCameraCapture = async (): Promise<boolean> => {
  // Check if it's a mobile/tablet device
  const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );

  if (!isMobileDevice) {
    return false;
  }

  // Also verify the device has a camera
  return await hasRealCamera();
};
